/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { NewCoreIconProps } from '@atlaskit/icon';
import AiGenerativeSlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

export const SLIDE_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('slide');

const isSlideUrl = (url: string): boolean =>
	// @ts-ignore - TS1503 TypeScript 5.9.2 upgrade
	url.match(/\/wiki\/spaces\/?.*\/slide\/(?<resourceId>\d+)(\?\/)?/) ||
	url.match(
		// @ts-ignore - TS1503 TypeScript 5.9.2 upgrade
		/\/wiki\/spaces\/?.*\/slide\/(?<resourceId>[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})(\?\/)?/,
	);

/**
 * Experience manifest for a slide embed.
 * Register with getNativeEmbedFacade().registerExperience(SlideExperienceManifest)
 * to obtain a Experience for EmbedFrameUI.
 */
export const SlideExperienceManifest: {
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly queryParams: {
		readonly disableCanvasScroll: 'true';
	};
	readonly supportedFeatures: Set<string>;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: {
			readonly onAskRovoClick: (parameters: NativeEmbedParameterValues) => void;
			readonly onSummariseClick: (parameters: NativeEmbedParameterValues) => void;
		};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
	};
} = {
	id: SLIDE_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {
		// TODO: Handle slide-specific commands if needed
	},
	isUrlMatch: isSlideUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	queryParams: { disableCanvasScroll: 'true' },
	isActive: (): true => {
		return true;
	},
	uiConfig: {
		editorToolbarActions: createEditorToolbarActions({
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				BUILTIN_TOOLBAR_KEYS.SUMMARISE,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.EDIT_URL,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		editorToolbarHandlers: {
			onAskRovoClick: (parameters: NativeEmbedParameterValues): void => {
				if (parameters.url) {
					const url = new URL(parameters.url);
					url.searchParams.set('rovoChatTriggerOpen', 'true');
					window.open(url.toString(), '_blank', 'noopener,noreferrer');
				}
			},
			onSummariseClick: (parameters: NativeEmbedParameterValues): void => {
				if (parameters.url) {
					const url = new URL(parameters.url);
					url.searchParams.set('rovoSummariseTriggerOpen', 'true');
					window.open(url.toString(), '_blank', 'noopener,noreferrer');
				}
			},
		},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay animationDisabled={props.animationDisabled} testId={props.testId} />
		),
		titleIcon: AiGenerativeSlidesIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
